import React, { Suspense, lazy, useMemo } from 'react';
import { RichTextElement } from 'coop-adventure-engine';
import Button from './Button';

/** Lazy-loaded to avoid a static import cycle (RichTextRenderer imports InlineOption). */
const RichTextRendererLazy = lazy(() =>
  import('./RichTextRenderer').then((m) => ({ default: m.RichTextRenderer }))
);

export interface InlineOptionProps {
  element: RichTextElement;
  onOptionClick: (optionId: string) => void;
  disabled?: boolean;
  className?: string;
  customColor?: string; // Optional custom button color from parent
}

const InlineOption: React.FC<InlineOptionProps> = ({
  element,
  onOptionClick,
  disabled = false,
  className = '',
  customColor
}) => {
  // Ensure this is an inline-option element
  if ((element as any).type !== 'inline-option') {
    return null;
  }

  const { content } = element;

  // Get the optionId from the element - this should be set during parsing
  const optionId = (element as any).optionId;
  // When display=block, style as full-width option (same component/structure as OptionButton)
  const fullWidth = (element as any).display === 'block';

  if (!optionId) {
    console.warn('InlineOption: Missing optionId property');
    return null;
  }

  const handleClick = () => {
    if (!disabled) {
      onOptionClick(optionId);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleClick();
    }
  };

  const innerDescription = useMemo(() => {
    if (!content || !Array.isArray(content)) {
      return { elements: [] as RichTextElement[] };
    }
    return { elements: content as RichTextElement[] };
  }, [content]);

  const richTextInner = (
    <Suspense fallback={null}>
      <RichTextRendererLazy
        description={innerDescription}
        {...(customColor ? { customColor } : {})}
        disableInlineOptions={disabled}
        className="option-rich-text"
      />
    </Suspense>
  );

  // Full-width: same Button as OptionButton; wrapper breaks out of paragraph indent when inside rich text
  if (fullWidth) {
    return (
      <div className={`inline-option-full-width-wrapper space-y-2 ${className}`}>
        <Button
          variant="glass-primary"
          size="md"
          fullWidth
          disabled={disabled}
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          {...(customColor ? { customColor } : {})}
          className="min-h-[48px] text-center justify-center"
          data-testid="inline-option"
          data-option-id={optionId}
          aria-label={`Option: ${optionId}`}
        >
          <div className="flex flex-col items-center w-full">
            <div className="font-medium option-rich-text w-full">{richTextInner}</div>
          </div>
        </Button>
      </div>
    );
  }

  // Inline (default): custom button with compact styling
  const getCustomColorStyles = () => {
    if (!customColor) return {};

    const getColorWithAlpha = (color: string, alpha: string) => {
      if (color.startsWith('#')) {
        return `${color}${alpha}`;
      }
      return color;
    };

    return {
      background: `linear-gradient(135deg, ${getColorWithAlpha(customColor, '88')}, ${getColorWithAlpha(customColor, 'AA')})`,
      backdropFilter: 'blur(12px) saturate(150%)',
      WebkitBackdropFilter: 'blur(12px) saturate(150%)',
      border: `1px solid ${getColorWithAlpha(customColor, '40')}`,
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
      WebkitFontSmoothing: 'antialiased' as const,
      MozOsxFontSmoothing: 'grayscale' as const,
    };
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      disabled={disabled}
      className={`inline-option ${customColor ? 'custom-color' : ''} ${className}`}
      style={getCustomColorStyles()}
      aria-label={`Option: ${optionId}`}
      role="button"
      data-testid="inline-option"
      data-option-id={optionId}
    >
      <div className="inline-option-content">{richTextInner}</div>
    </button>
  );
};

export default InlineOption;
